import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2, RefreshCw, Eye, Send, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  adminListEmailTemplates,
  adminPreviewEmailTemplate,
  adminSendEmailTemplateTest,
} from '../lib/api';

/**
 * Admin · Email Templates preview
 *
 * `/admin/email-templates` — lets an admin:
 *   1. Pick one of the known Resend templates (welcome / confirmation /
 *      password-reset).
 *   2. See the current template id from the backend env so drift with
 *      the Resend dashboard is easy to catch.
 *   3. Fill each detected merge tag with a test value (pre-populated
 *      with a sensible default so a preview renders on first paint).
 *   4. Preview the exact HTML the recipient would see — rendered
 *      server-side into a sandboxed iframe here so styles don't collide
 *      with the admin app chrome.
 *   5. Optionally send a real test email through the same production
 *      send function used at signup / password-reset. Dashboard template
 *      first, inline HTML fallback if Resend rejects (matches live
 *      behaviour byte-for-byte).
 */
const AdminEmailTemplates = () => {
  const { user, loading: authLoading } = useAuth();

  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState('welcome');
  const [variables, setVariables] = useState({});
  const [testTo, setTestTo] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [loading, setLoading] = useState(true);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null); // { tone: 'ok'|'err', text }

  // Reset banner whenever the admin changes the template so old
  // success / error messages don't linger under the new form.
  useEffect(() => { setBanner(null); }, [selectedKey]);

  // Boot: fetch template registry + pre-fill sensible defaults into
  // `variables` for the initial selection.
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminListEmailTemplates();
        if (cancelled) return;
        const list = Array.isArray(data?.templates) ? data.templates : [];
        setTemplates(list);
        // Seed the form with the FIRST template's defaults so
        // "Refresh preview" renders straight away.
        const first = list[0];
        if (first) {
          setSelectedKey(first.key);
          setVariables(defaultsFor(first));
        }
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'Failed to load templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Whenever the admin switches templates, replace the variable map
  // with defaults for the new template so the previous template's
  // orphan keys don't leak into the preview payload.
  const currentTemplate = useMemo(
    () => templates.find((t) => t.key === selectedKey) || null,
    [templates, selectedKey],
  );
  useEffect(() => {
    if (currentTemplate) {
      setVariables(defaultsFor(currentTemplate));
      setPreviewHtml('');
      setPreviewSubject('');
    }
  }, [selectedKey, currentTemplate]);

  const updateVariable = (key, value) => {
    setVariables((prev) => ({ ...prev, [key]: value }));
  };

  const doPreview = async () => {
    if (!selectedKey) return;
    setPreviewBusy(true);
    setError(null);
    try {
      const data = await adminPreviewEmailTemplate(selectedKey, variables);
      setPreviewHtml(data?.html || '');
      setPreviewSubject(data?.subject || '');
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Preview failed');
    } finally {
      setPreviewBusy(false);
    }
  };

  const doSendTest = async () => {
    const to = testTo.trim();
    if (!to || !selectedKey) return;
    setBanner(null);
    setSendBusy(true);
    setError(null);
    try {
      await adminSendEmailTemplateTest(selectedKey, to, variables);
      setBanner({ tone: 'ok', text: `Test email dispatched to ${to}. Check the inbox (may take a few seconds).` });
    } catch (e) {
      setBanner({ tone: 'err', text: e?.response?.data?.detail || e.message || 'Send failed' });
    } finally {
      setSendBusy(false);
    }
  };

  if (authLoading) return <div className="p-8 text-center text-zinc-500">Loading…</div>;
  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">Admin only</h1>
        <Link to="/" className="text-amber-600 hover:underline">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="admin-email-templates-page">
      <div className="flex items-center gap-3 mb-6">
        <Mail size={22} className="text-amber-500" />
        <h1 className="text-2xl font-bold" data-testid="admin-email-templates-title">Admin · Email Templates</h1>
        <button
          type="button"
          onClick={() => window.location.reload()}
          disabled={loading}
          data-testid="reload-templates-btn"
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Reload
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm mb-4" data-testid="admin-templates-error">
          <AlertTriangle size={14} className="inline mr-1" /> {error}
        </div>
      )}
      {banner && (
        <div
          data-testid="admin-templates-banner"
          className={`p-3 rounded-lg text-sm mb-4 flex items-center gap-2 ${
            banner.tone === 'ok'
              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          }`}
        >
          {banner.tone === 'ok' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
          {banner.text}
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-zinc-500">
          <Loader2 className="animate-spin inline mr-2" size={16} /> Loading templates…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* -------- Left column: form -------- */}
          <div className="lg:col-span-2 space-y-4">
            {/* Template picker */}
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4">
              <label className="block text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Template
              </label>
              <select
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
                data-testid="template-picker"
                className="w-full h-10 px-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
              >
                {templates.map((t) => (
                  <option key={t.key} value={t.key}>{t.label} — {t.key}</option>
                ))}
              </select>
              {currentTemplate && (
                <div className="mt-3 text-xs text-zinc-500 space-y-1">
                  <div>
                    <span className="font-semibold text-zinc-700 dark:text-zinc-300">Subject:</span>{' '}
                    <span data-testid="template-subject">{currentTemplate.subject}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-zinc-700 dark:text-zinc-300">Resend ID:</span>{' '}
                    <code
                      className="font-mono text-amber-700 dark:text-amber-400"
                      data-testid="template-resend-id"
                    >{currentTemplate.template_id || '(unset)'}</code>
                    <span className="ml-1 text-zinc-400">from</span>{' '}
                    <code className="font-mono">{currentTemplate.env_var}</code>
                  </div>
                </div>
              )}
            </div>

            {/* Merge tag inputs */}
            {currentTemplate && (
              <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">
                  Merge tags
                </div>
                <div className="space-y-3">
                  {currentTemplate.variables.map((v) => (
                    <div key={v.key}>
                      <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                        {v.label} <code className="font-mono text-[10px] text-zinc-500 ml-1">{`{{${v.key}}}`}</code>
                      </label>
                      <input
                        type="text"
                        value={variables[v.key] ?? ''}
                        onChange={(e) => updateVariable(v.key, e.target.value)}
                        data-testid={`var-input-${v.key}`}
                        className="w-full h-9 px-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={doPreview}
                  disabled={previewBusy}
                  data-testid="preview-btn"
                  className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 hover:opacity-90 disabled:opacity-50"
                >
                  {previewBusy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                  Refresh preview
                </button>
              </div>
            )}

            {/* Send test */}
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">
                Send test email
              </div>
              <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                Recipient
              </label>
              <input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="admin@yourdomain.com"
                data-testid="test-recipient-input"
                className="w-full h-9 px-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
              />
              <button
                type="button"
                onClick={doSendTest}
                disabled={sendBusy || !testTo.trim() || !selectedKey}
                data-testid="send-test-btn"
                className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send to {testTo || '…'}
              </button>
              <p className="mt-2 text-[11px] text-zinc-500 leading-snug">
                Uses the live send function — Resend template first, inline HTML fallback if Resend rejects. Same code path as real signup / password-reset emails.
              </p>
            </div>
          </div>

          {/* -------- Right column: rendered preview -------- */}
          <div className="lg:col-span-3">
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4">
              <div className="flex items-baseline gap-2 mb-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Preview</div>
                {previewSubject && (
                  <div className="text-xs text-zinc-500 ml-auto truncate" data-testid="preview-subject">
                    Subject: <span className="font-medium text-zinc-700 dark:text-zinc-300">{previewSubject}</span>
                  </div>
                )}
              </div>
              {previewHtml ? (
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  data-testid="preview-iframe"
                  className="w-full h-[720px] rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white"
                />
              ) : (
                <div className="py-16 text-center text-zinc-500 text-sm" data-testid="preview-empty">
                  Fill in the merge tags on the left and tap <span className="font-medium">Refresh preview</span> to render this template.
                </div>
              )}
              <p className="mt-2 text-[11px] text-zinc-500 leading-snug">
                This is the inline HTML fallback — visually mirrors the Resend dashboard template. If Resend accepts the template send at runtime, the recipient sees the dashboard version; if it fails, they see this exact HTML.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
        <Link to="/admin/feedback" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Feedback</Link>
        <Link to="/admin/users" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Users</Link>
        <Link to="/admin/activity" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Activity</Link>
      </div>
    </div>
  );
};

/** Return `{key: default}` object for every variable this template
 * declares. Called on load and on template-switch so the merge-tag
 * form is never empty. */
function defaultsFor(template) {
  const map = {};
  for (const v of template?.variables || []) map[v.key] = v.default ?? '';
  return map;
}

export default AdminEmailTemplates;
